import React, { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { NursingHome } from "@/hooks/use-nursing-home";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { MessageSquare, Mail, Clock, Send, ChevronDown, ChevronUp } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/auth/AuthProvider";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { format } from "date-fns";

type ContextType = { nursingHome: NursingHome };

export default function MessagesPage() {
  const { nursingHome } = useOutletContext<ContextType>();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Fetch all messages (threads) for this nursing home
  const { data: threads = [], isLoading } = useQuery({
    queryKey: ["owner-messages", nursingHome.nh_slug],
    queryFn: async () => {
      // Get root messages (thread starters)
      const { data: rootMessages, error } = await supabase
        .from("messages")
        .select("*")
        .eq("nursing_home_id", nursingHome.nh_slug)
        .is("thread_id", null)
        .order("created_at", { ascending: false });
      if (error) throw error;

      // Get replies for each thread
      const threadIds = (rootMessages || []).map((m: any) => m.id);
      let replies: any[] = [];
      if (threadIds.length > 0) {
        const { data: replyData } = await supabase
          .from("messages")
          .select("*")
          .in("thread_id", threadIds)
          .order("created_at", { ascending: true });
        replies = replyData || [];
      }

      return (rootMessages || []).map((msg: any) => ({
        ...msg,
        replies: replies.filter((r: any) => r.thread_id === msg.id),
      }));
    },
  });

  // Mark as read
  const markRead = useMutation({
    mutationFn: async (messageId: string) => {
      await supabase
        .from("messages")
        .update({ is_read: true })
        .eq("id", messageId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["owner-messages"] });
    },
  });

  const unreadCount = threads.filter((t: any) => !t.is_read).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <MessageSquare className="h-6 w-6" />
            Messages
          </h1>
          <p className="text-muted-foreground">
            Messages from families enquiring about {nursingHome.center_name}
          </p>
        </div>
        {unreadCount > 0 && (
          <Badge variant="destructive">{unreadCount} unread</Badge>
        )}
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
        </div>
      ) : threads.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Mail className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-1">No messages yet</h3>
            <p className="text-muted-foreground">
              When families send you messages through your profile, they'll appear here.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {threads.map((thread: any) => (
            <ThreadCard
              key={thread.id}
              thread={thread}
              nhSlug={nursingHome.nh_slug}
              userId={user?.id}
              onMarkRead={() => markRead.mutate(thread.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ThreadCard({
  thread,
  nhSlug,
  userId,
  onMarkRead,
}: {
  thread: any;
  nhSlug: string;
  userId?: string;
  onMarkRead: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const queryClient = useQueryClient();

  const handleExpand = () => {
    if (!expanded && !thread.is_read) {
      onMarkRead();
    }
    setExpanded(!expanded);
  };

  const sendReply = async () => {
    if (!replyText.trim() || !userId) return;
    setSending(true);
    try {
      const { data: profile } = await supabase
        .from("profiles")
        .select("first_name, last_name")
        .eq("id", userId)
        .single();

      const senderName = profile
        ? `${profile.first_name || ""} ${profile.last_name || ""}`.trim()
        : "Nursing Home";

      const { error } = await supabase.from("messages").insert({
        nursing_home_id: nhSlug,
        thread_id: thread.id,
        sender_type: "owner",
        sender_name: senderName,
        sender_email: "",
        sender_user_id: userId,
        subject: `Re: ${thread.subject}`,
        body: replyText.trim(),
      });

      if (error) throw error;

      // Notify the family via email
      try {
        await supabase.functions.invoke("notify-new-message", {
          body: {
            nursing_home_id: nhSlug,
            nursing_home_name: senderName,
            sender_name: senderName,
            sender_email: thread.sender_email,
            subject: `Re: ${thread.subject}`,
            message: replyText.trim(),
            is_reply: true,
            reply_to_email: thread.sender_email,
          },
        });
      } catch {
        console.warn("Reply email notification failed");
      }

      setReplyText("");
      toast.success("Reply sent!");
      queryClient.invalidateQueries({ queryKey: ["owner-messages"] });
    } catch (error) {
      toast.error("Failed to send reply");
    } finally {
      setSending(false);
    }
  };

  return (
    <Card className={!thread.is_read ? "border-primary/50 bg-primary/5" : ""}>
      <CardContent className="p-4">
        <div
          className="flex items-start justify-between cursor-pointer"
          onClick={handleExpand}
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              {!thread.is_read && (
                <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
              )}
              <span className="font-medium text-sm truncate">
                {thread.sender_name}
              </span>
              <span className="text-xs text-muted-foreground">
                &lt;{thread.sender_email}&gt;
              </span>
            </div>
            <p className="text-sm font-medium mt-0.5">{thread.subject}</p>
            {!expanded && (
              <p className="text-sm text-muted-foreground truncate mt-0.5">
                {thread.body.slice(0, 100)}...
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 ml-2">
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {format(new Date(thread.created_at), "MMM d")}
            </span>
            {thread.replies?.length > 0 && (
              <Badge variant="secondary" className="text-xs">
                {thread.replies.length} {thread.replies.length === 1 ? "reply" : "replies"}
              </Badge>
            )}
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </div>
        </div>

        {expanded && (
          <div className="mt-4 space-y-3">
            {/* Original message */}
            <div className="bg-muted/50 rounded-lg p-3">
              <p className="text-sm whitespace-pre-line">{thread.body}</p>
              <p className="text-xs text-muted-foreground mt-2">
                {format(new Date(thread.created_at), "PPp")}
              </p>
            </div>

            {/* Replies */}
            {thread.replies?.map((reply: any) => (
              <div
                key={reply.id}
                className={`rounded-lg p-3 ${
                  reply.sender_type === "owner"
                    ? "bg-grandpal-green/10 ml-4"
                    : "bg-muted/50"
                }`}
              >
                <div className="flex items-center gap-1.5 mb-1">
                  <span className="text-xs font-medium">
                    {reply.sender_name}
                  </span>
                  <Badge variant="outline" className="text-[10px] px-1 py-0">
                    {reply.sender_type}
                  </Badge>
                </div>
                <p className="text-sm whitespace-pre-line">{reply.body}</p>
                <p className="text-xs text-muted-foreground mt-2">
                  {format(new Date(reply.created_at), "PPp")}
                </p>
              </div>
            ))}

            {/* Reply input */}
            <div className="flex gap-2 mt-3">
              <Textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Write a reply..."
                rows={2}
                className="flex-1"
              />
              <Button
                size="sm"
                onClick={sendReply}
                disabled={!replyText.trim() || sending}
                className="self-end"
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
